import React, { FC, useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import {
  LastSchriftDetailsDto,
  SEPAExportOptions,
  AccountSEPAData,
} from "../types/sepa-export.types";

interface SEPAExportDialogProps {
  open: boolean;
  onClose: () => void;
  selectedTransactions: LastSchriftDetailsDto[];
  accountSepaData?: AccountSEPAData[];
  onExport: (exportOptions: SEPAExportOptions) => Promise<void>;
  loading?: boolean;
}

const SEPAExportDialog: FC<SEPAExportDialogProps> = ({
  open,
  onClose,
  selectedTransactions,
  accountSepaData = [],
  onExport,
  loading = false,
}) => {
  const [exportOptions, setExportOptions] = useState<SEPAExportOptions>({
    messageId: `SEPA-${Date.now()}`,
    creditorName: "",
    creditorIBAN: "",
    creditorBIC: "",
    creditorId: "",
    creationDateTime: new Date(),
  });

  const [selectedAccountId, setSelectedAccountId] = useState<string>("");
  const [errors, setErrors] = useState<Partial<SEPAExportOptions>>({});

  // Auto-select account and pre-fill SEPA data if only one account
  useEffect(() => {
    if (accountSepaData.length === 1) {
      const accountData = accountSepaData[0];
      setSelectedAccountId(accountData.accountId);
      prefillSEPAData(accountData);
    } else if (accountSepaData.length > 1) {
      // Reset fields if multiple accounts
      setSelectedAccountId("");
      setExportOptions((prev) => ({
        ...prev,
        creditorName: "",
        creditorIBAN: "",
        creditorBIC: "",
        creditorId: "",
      }));
    }
  }, [accountSepaData]);

  const prefillSEPAData = (accountData: AccountSEPAData) => {
    setExportOptions((prev) => ({
      ...prev,
      creditorName:
        accountData.creditorName ||
        accountData.accountFullName ||
        accountData.accountName ||
        "",
      creditorIBAN: accountData.creditorIBAN || "",
      creditorBIC: accountData.creditorBIC || "",
      creditorId: accountData.creditorId || "",
    }));
  };

  const handleAccountChange = (event: SelectChangeEvent<string>) => {
    const accountId = event.target.value;
    setSelectedAccountId(accountId);

    const accountData = accountSepaData.find(
      (acc) => acc.accountId === accountId,
    );
    if (accountData) {
      prefillSEPAData(accountData);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<SEPAExportOptions> = {};

    if (!exportOptions.creditorName.trim()) {
      newErrors.creditorName = "Gläubiger Name ist erforderlich";
    }

    if (!exportOptions.creditorIBAN.trim()) {
      newErrors.creditorIBAN = "IBAN ist erforderlich";
    } else if (
      !/^[A-Z]{2}[0-9]{2}[A-Z0-9]{4}[0-9]{7}([A-Z0-9]?){0,16}$/.test(
        exportOptions.creditorIBAN.replace(/\s/g, ""),
      )
    ) {
      newErrors.creditorIBAN = "Ungültiges IBAN-Format";
    }

    // BIC is optional; validate only if provided
    if (exportOptions.creditorBIC && exportOptions.creditorBIC.trim()) {
      if (
        !/^[A-Z]{6}[A-Z2-9][A-NP-Z0-9]([A-Z0-9]{3})?$/.test(
          exportOptions.creditorBIC,
        )
      ) {
        newErrors.creditorBIC = "Ungültiges BIC-Format";
      }
    }

    if (!exportOptions.creditorId.trim()) {
      newErrors.creditorId = "Gläubiger-ID ist erforderlich";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleExport = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      await onExport(exportOptions);
      onClose();
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  const totalAmount = selectedTransactions.reduce((sum, transaction) => {
    return sum + Number(transaction.amount);
  }, 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>SEPA LastSchrift Export</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            {selectedTransactions.length} Transaktionen ausgewählt
            <br />
            Gesamtbetrag: {totalAmount.toFixed(2).replace(".", ",")} €
          </Alert>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Warning if no account data available */}
          {accountSepaData.length === 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>Warnung:</strong> Keine Mandantendaten für automatische
              Befüllung verfügbar.
              <br />
              Bitte füllen Sie die SEPA-Parameter manuell aus.
            </Alert>
          )}

          {/* Account Selection - only show if multiple accounts */}
          {accountSepaData.length > 1 && (
            <FormControl fullWidth>
              <InputLabel id="account-select-label">
                Mandant auswählen
              </InputLabel>
              <Select
                labelId="account-select-label"
                value={selectedAccountId}
                onChange={handleAccountChange}
                label="Mandant auswählen"
                required
              >
                {accountSepaData.map((account) => (
                  <MenuItem key={account.accountId} value={account.accountId}>
                    {account.accountFullName || account.accountName} (
                    {account.transactionCount} Transaktionen)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Account Info Display - show selected account info */}
          {accountSepaData.length === 1 && (
            <Alert severity="info" sx={{ mb: 1 }}>
              <strong>Mandant:</strong>{" "}
              {accountSepaData[0].accountFullName ||
                accountSepaData[0].accountName}
              {/* <br />
              <strong>Transaktionen:</strong>{" "}
              {accountSepaData[0].transactionCount} */}
            </Alert>
          )}

          <TextField
            label="Nachrichten-ID"
            value={exportOptions.messageId}
            onChange={(e) =>
              setExportOptions({ ...exportOptions, messageId: e.target.value })
            }
            fullWidth
            required
            disabled
            helperText="Automatisch generiert"
          />

          <TextField
            label="Gläubiger Name"
            value={exportOptions.creditorName}
            onChange={(e) =>
              setExportOptions({
                ...exportOptions,
                creditorName: e.target.value,
              })
            }
            fullWidth
            required
            error={!!errors.creditorName}
            helperText={
              errors.creditorName || "Automatisch befüllt aus Mandantendaten"
            }
            placeholder="z.B. Physiotherapie Muster GmbH"
          />

          <TextField
            label="Gläubiger IBAN"
            value={exportOptions.creditorIBAN}
            onChange={(e) =>
              setExportOptions({
                ...exportOptions,
                creditorIBAN: e.target.value.replace(/\s/g, "").toUpperCase(),
              })
            }
            fullWidth
            required
            error={!!errors.creditorIBAN}
            helperText={
              errors.creditorIBAN || "Automatisch befüllt aus Mandantendaten"
            }
            placeholder="DE89370400440532013000"
          />

          <TextField
            label="Gläubiger BIC"
            value={exportOptions.creditorBIC}
            onChange={(e) =>
              setExportOptions({
                ...exportOptions,
                creditorBIC: e.target.value.toUpperCase(),
              })
            }
            fullWidth
            // BIC optional
            error={!!errors.creditorBIC}
            helperText={
              errors.creditorBIC ||
              "Optional; wird nur benötigt, wenn verfügbar"
            }
            placeholder="COBADEFFXXX"
          />

          <TextField
            label="Gläubiger-ID"
            value={exportOptions.creditorId}
            onChange={(e) =>
              setExportOptions({ ...exportOptions, creditorId: e.target.value })
            }
            fullWidth
            required
            error={!!errors.creditorId}
            helperText={
              errors.creditorId || "Automatisch befüllt aus Mandantendaten"
            }
            placeholder="DE98ZZZ09999999999"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Abbrechen
        </Button>
        <Button
          onClick={handleExport}
          variant="contained"
          disabled={loading || selectedTransactions.length === 0}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? "Exportiere..." : "SEPA Export"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SEPAExportDialog;
